import type { CalendarVisitGroup } from "@calendar/groupCalendarVisits";

const DEFAULT_DURATION_MS = 30 * 60 * 1000;

const dateFormatters = new Map<string, Intl.DateTimeFormat>();

// The clinic API's `timeZone` is unvalidated and an unknown zone throws; fall back to browser-local.
const calendarParts = (date: Date, timeZone: string) => {
  let formatter = dateFormatters.get(timeZone);
  if (!formatter) {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hourCycle: "h23",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    };
    try {
      formatter = new Intl.DateTimeFormat("en-US", { ...options, timeZone });
    } catch {
      formatter = new Intl.DateTimeFormat("en-US", options);
    }
    dateFormatters.set(timeZone, formatter);
  }
  return formatter.formatToParts(date);
};

export const calendarDateKey = (date: Date, timeZone: string): string => {
  const parts = calendarParts(date, timeZone);
  const part = (type: string) => parts.find((p) => p.type === type)!.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
};

const decimalHour = (date: Date, timeZone: string): number => {
  const parts = calendarParts(date, timeZone);
  return (
    Number(parts.find((p) => p.type === "hour")!.value) +
    Number(parts.find((p) => p.type === "minute")!.value) / 60
  );
};

const isMidnight = (date: Date, timeZone: string): boolean =>
  date.getUTCMilliseconds() === 0 &&
  calendarParts(date, timeZone)
    .filter((part) => ["hour", "minute", "second"].includes(part.type))
    .every((part) => Number(part.value) === 0);

// Same unvalidated-timezone guard as `calendarParts`.
const localized = (
  date: Date,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): string => {
  try {
    return date.toLocaleString([], { ...options, timeZone });
  } catch {
    return date.toLocaleString([], options);
  }
};

export const calendarTime = (iso: string, timeZone: string): string =>
  localized(new Date(iso), timeZone, {
    hour: "numeric",
    minute: "2-digit",
  });

export const calendarClock = (iso: string, timeZone: string): string => {
  const parts = calendarParts(new Date(iso), timeZone);
  const part = (type: string) => parts.find((p) => p.type === type)!.value;
  return `${Number(part("hour")) % 12 || 12}:${part("minute")}`;
};

export const calendarDateTime = (iso: string, timeZone: string): string =>
  localized(new Date(iso), timeZone, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export interface DaySegment {
  group: CalendarVisitGroup;
  start: number;
  end: number;
  continuesBefore: boolean;
  continuesAfter: boolean;
}

export interface StayBanner {
  group: CalendarVisitGroup;
  firstDay: number;
  lastDay: number;
  row: number;
  continuesBefore: boolean;
  continuesAfter: boolean;
}

export const calendarLayout = (
  groups: CalendarVisitGroup[],
  days: string[],
  timeZone: string,
): { timed: DaySegment[][]; stays: StayBanner[] } => {
  const timed: DaySegment[][] = days.map(() => []);
  const stays: StayBanner[] = [];
  for (const group of groups) {
    const visit = group.visits[0];
    if (!visit.scheduledStart) continue;
    const start = new Date(visit.scheduledStart);
    const end = new Date(
      visit.scheduledEnd ?? start.getTime() + DEFAULT_DURATION_MS,
    );
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()))
      continue;
    if (end <= start) continue;
    const startDay = calendarDateKey(start, timeZone);
    const endDay = calendarDateKey(end, timeZone);
    // The exclusive end must not add a day when checkout is exactly midnight.
    const lastDay = calendarDateKey(new Date(end.getTime() - 1), timeZone);
    const firstIndex = days.findIndex(
      (day) => day >= startDay && day <= lastDay,
    );
    if (firstIndex < 0) continue;
    let lastIndex = firstIndex;
    while (lastIndex + 1 < days.length && days[lastIndex + 1] <= lastDay)
      lastIndex++;
    const isFullDay =
      startDay < endDay &&
      isMidnight(start, timeZone) &&
      isMidnight(end, timeZone);
    if (visit.scheduledEnd && (startDay < lastDay || isFullDay)) {
      stays.push({
        group,
        firstDay: firstIndex,
        lastDay: lastIndex,
        row: 0,
        continuesBefore: startDay < days[0],
        continuesAfter: lastDay > days[days.length - 1],
      });
      continue;
    }
    for (let i = firstIndex; i <= lastIndex; i++) {
      timed[i].push({
        group,
        start: startDay < days[i] ? 0 : decimalHour(start, timeZone),
        end: endDay > days[i] ? 24 : decimalHour(end, timeZone),
        continuesBefore: startDay < days[i],
        continuesAfter: lastDay > days[i],
      });
    }
  }
  stays.sort(
    (a, b) =>
      a.firstDay - b.firstDay ||
      b.lastDay - a.lastDay ||
      a.group.visits[0].scheduledStart!.localeCompare(
        b.group.visits[0].scheduledStart!,
      ) ||
      a.group.visits[0].id.localeCompare(b.group.visits[0].id),
  );
  const rowEnds: number[] = [];
  for (const stay of stays) {
    const available = rowEnds.findIndex((end) => end < stay.firstDay);
    stay.row = available < 0 ? rowEnds.length : available;
    rowEnds[stay.row] = stay.lastDay;
  }
  return { timed, stays };
};
